import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import store from "./store";
import AppRoutes from "./routes";
import { TranslationProvider } from "./contexts/TranslationContext";
import WebSocketManager from "./components/common/WebSocketManager";
import MaintenanceMode from "./components/common/MaintenanceMode";
import maintenanceMode from "./utils/maintenanceMode";
import { MdSupportAgent } from "react-icons/md";
import { useState, useEffect, useMemo, useRef } from "react";
import ContactSupportModal from "./components/common/ContactSupportModal";
import SuccessModal from "./components/common/SuccessModal";

// Movement under this threshold is treated as a click, not a drag.
const DRAG_THRESHOLD_PX = 5;
// After a real drag ends, ignore any clicks fired by the browser for
// this many ms (covers synthesized clicks on touch + edge cases where
// a pointerup fires straight into a click on the same target).
const POST_DRAG_CLICK_SUPPRESS_MS = 400;
// Default distance of the icon from the right / bottom edges.
const EDGE_INSET_PX = 24;
const BUTTON_SIZE_PX = 64;

// The icon's position is stored as INSETS from the right and bottom edges
// (not absolute left/top). Rendered via CSS `right`/`bottom`, the browser
// keeps it anchored to the side automatically at EVERY width — so it never
// slides toward the middle on resize (the old left/top model recomputed on
// every resize event and drifted). Clamp keeps it fully on-screen.
function clampInset(inset, vw, vh) {
  const width = vw ?? (typeof window === "undefined" ? null : window.innerWidth);
  const height = vh ?? (typeof window === "undefined" ? null : window.innerHeight);
  if (width == null || height == null) return inset;
  const maxRight = Math.max(0, width - BUTTON_SIZE_PX);
  const maxBottom = Math.max(0, height - BUTTON_SIZE_PX);
  return {
    right: Math.max(0, Math.min(inset.right, maxRight)),
    bottom: Math.max(0, Math.min(inset.bottom, maxBottom)),
  };
}

// At or below this width the viewport IS the content column, so an inset that
// merely sits "on screen" necessarily sits ON TOP of interactive controls.
const MOBILE_ANCHOR_MAX_WIDTH_PX = 640;

// anchorInset — the inset the icon actually RENDERS at.
//
// T023: clampInset only guarantees "fully on screen" (0 … viewport − 64). On a
// desktop that leaves the icon in a harmless margin, but on a 390×844 phone the
// same stored value parks it mid-page over the "Select a position" field or the
// feed card's action icons. The position model had no notion of being anchored:
// it stored a free 2D offset and clamped it once, at mount.
//
// So below the mobile breakpoint the rendered position resolves to the nearest
// BOTTOM corner — the side the user picked is kept, the vertical drops to the
// edge. The icon is never hidden and the user's stored desktop position is left
// untouched; only what is painted at mobile width changes. Above the breakpoint
// this is exactly clampInset, so desktop drag behaviour is unchanged.
function anchorInset(inset, vw, vh) {
  const width = vw ?? (typeof window === "undefined" ? null : window.innerWidth);
  if (width == null) return inset;
  const clamped = clampInset(inset, vw, vh);
  if (width > MOBILE_ANCHOR_MAX_WIDTH_PX) return clamped;
  const maxRight = Math.max(0, width - BUTTON_SIZE_PX);
  // Insets grow toward the left, so a large `right` means the left half.
  const onLeftHalf = clamped.right > maxRight / 2;
  return {
    right: onLeftHalf
      ? Math.max(0, maxRight - EDGE_INSET_PX)
      : Math.min(EDGE_INSET_PX, maxRight),
    bottom: EDGE_INSET_PX,
  };
}

export { clampInset, anchorInset, MOBILE_ANCHOR_MAX_WIDTH_PX, BUTTON_SIZE_PX, EDGE_INSET_PX };

const App = () => {
  const [showSupportModal, setShowSupportModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [supportPrefaceMessage, setSupportPrefaceMessage] = useState("");
  // Position as insets from the right/bottom edges (see clampInset). Rendered
  // with CSS right/bottom so the icon stays anchored to the side at all widths
  // without any resize handler.
  const [inset, setInset] = useState({
    right: EDGE_INSET_PX,
    bottom: EDGE_INSET_PX,
  });
  // isDragging is kept ONLY for the grab/grabbing cursor style. All the
  // drag-vs-click logic uses refs so it's never out of date due to React
  // batching (which is what caused the original bug — the click handler
  // was reading a stale state value and treating real drags as clicks).
  const [isDragging, setIsDragging] = useState(false);
  // Viewport size, kept fresh so the rendered inset can be re-derived when the
  // window resizes or the device rotates (see the resize effect below).
  const [viewport, setViewport] = useState(() => ({
    w: typeof window === "undefined" ? null : window.innerWidth,
    h: typeof window === "undefined" ? null : window.innerHeight,
  }));
  const supportButtonRef = useRef(null);

  // Drag-state refs — synchronous, available immediately to any handler.
  const isDraggingRef = useRef(false);            // live drag flag
  const pressStartPos = useRef({ x: 0, y: 0 });   // raw pointer at press start
  const insetAtPressRef = useRef({ right: 0, bottom: 0 }); // inset at press
  const hasDraggedRef = useRef(false);            // crossed the threshold?
  const suppressClickUntilRef = useRef(0);        // ms timestamp; ignore clicks before this
  const insetRef = useRef({ right: EDGE_INSET_PX, bottom: EDGE_INSET_PX }); // mirrors inset for save-on-end

  // Load saved insets on mount, clamped to the current viewport (so an inset
  // saved on a wide monitor can't push the icon off a small screen).
  // Migrates the legacy {x,y} (absolute left/top) format to {right,bottom}.
  useEffect(() => {
    const saved = localStorage.getItem("supportAgentPosition");
    let initial = { right: EDGE_INSET_PX, bottom: EDGE_INSET_PX };
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed.right === "number") {
          initial = clampInset(parsed);
        } else if (parsed && typeof parsed.x === "number") {
          // Legacy left/top → convert to right/bottom insets.
          initial = clampInset({
            right: window.innerWidth - parsed.x - BUTTON_SIZE_PX,
            bottom: window.innerHeight - parsed.y - BUTTON_SIZE_PX,
          });
        }
      } catch (error) {
        console.error("Error parsing saved position:", error);
      }
    }
    setInset(initial);
    insetRef.current = initial;
  }, []);

  // The resize listener does NOT recompute the position (that was the old
  // left/top model's bug — it drifted toward the middle on every resize). It
  // only records the viewport so the RENDERED inset can be re-derived: an inset
  // stored on a 1440px monitor is out of range on a 390px phone, and until
  // T023 nothing re-evaluated it after mount, so rotating or resizing left the
  // icon parked over page content (or off-screen entirely).
  useEffect(() => {
    const onResize = () =>
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  // What the icon is painted at. While a drag is in flight it follows the
  // finger/cursor unchanged (clamped only); the moment the drag ends — and on
  // every mount/resize — it re-anchors, which at mobile width means the nearest
  // bottom corner. Desktop is unaffected (anchorInset === clampInset there).
  const renderInset = useMemo(
    () =>
      isDragging
        ? clampInset(inset, viewport.w, viewport.h)
        : anchorInset(inset, viewport.w, viewport.h),
    [inset, isDragging, viewport],
  );

  const handleSupportSuccess = () => {
    setShowSuccessModal(true);
  };

  // -----------------------------------------------------------------
  // Pointer Events: a single unified API for mouse + touch + stylus.
  // Using these instead of mouse + touch event pairs eliminates the
  // synthesised-click-after-touch-drag bug (where mobile browsers fire
  // a phantom click after touchend, which the old code mis-classified
  // as a real tap and opened the modal). With setPointerCapture, all
  // pointermove / pointerup fire on the button itself, so the global-
  // document listeners + their cleanup-on-every-position-change dance
  // also goes away.
  // -----------------------------------------------------------------
  const handlePointerDown = (e) => {
    // Only respond to primary button / single touch. Ignore right-click,
    // middle-click, multi-touch pinch, etc.
    if (e.button !== undefined && e.button !== 0) return;

    isDraggingRef.current = true;
    hasDraggedRef.current = false;
    pressStartPos.current = { x: e.clientX, y: e.clientY };
    // Drag from where the icon is PAINTED, not from the stored inset. At mobile
    // width those differ (the stored value is re-anchored to a corner for
    // rendering), and seeding from the stored value would make the icon jump
    // under the finger the instant a drag started.
    insetAtPressRef.current = { ...renderInset };
    insetRef.current = { ...renderInset };
    setIsDragging(true); // for the grab/grabbing cursor

    // Capture the pointer so move/up fire on this element even if the
    // user drags off the button. Browsers without setPointerCapture
    // fall through silently — touchAction:none on the element keeps
    // page-scroll suppressed during drag in both code paths.
    if (e.currentTarget && e.currentTarget.setPointerCapture) {
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch (err) {
        /* no-op */
      }
    }
    e.preventDefault();
  };

  const handlePointerMove = (e) => {
    if (!isDraggingRef.current) return;
    const dx = e.clientX - pressStartPos.current.x;
    const dy = e.clientY - pressStartPos.current.y;
    if (
      !hasDraggedRef.current &&
      (Math.abs(dx) > DRAG_THRESHOLD_PX || Math.abs(dy) > DRAG_THRESHOLD_PX)
    ) {
      hasDraggedRef.current = true;
    }

    // Insets grow toward the top-left, so moving right/down shrinks them.
    const next = clampInset({
      right: insetAtPressRef.current.right - dx,
      bottom: insetAtPressRef.current.bottom - dy,
    });
    insetRef.current = next;
    setInset(next);
  };

  const handlePointerUpOrCancel = (e) => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    setIsDragging(false);

    // If the user actually dragged (crossed the threshold) save the
    // new position and block the immediately-following click. Otherwise
    // this was just a tap-down-up → fall through to the click handler.
    if (hasDraggedRef.current) {
      try {
        localStorage.setItem(
          "supportAgentPosition",
          JSON.stringify(insetRef.current),
        );
      } catch (err) {
        /* ignore quota errors */
      }
      suppressClickUntilRef.current = Date.now() + POST_DRAG_CLICK_SUPPRESS_MS;
    }

    if (e.currentTarget && e.currentTarget.releasePointerCapture) {
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch (err) {
        /* no-op */
      }
    }
  };

  const handleButtonClick = (e) => {
    // Suppress any click inside the post-drag window. This covers BOTH
    // the immediate click that follows pointerup AND mobile-browser
    // synthesised clicks that arrive several hundred ms later.
    if (Date.now() < suppressClickUntilRef.current) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    setShowSupportModal(true);
  };

  // Listen for requests to open the support modal with a message
  useEffect(() => {
    const handleOpenSupport = (e) => {
      const message = e?.detail?.message || "";
      setSupportPrefaceMessage(message);
      setShowSupportModal(true);
    };
    window.addEventListener("openSupportModal", handleOpenSupport);
    return () =>
      window.removeEventListener("openSupportModal", handleOpenSupport);
  }, []);

  // Maintenance Mode Check - This takes priority over everything
  if (maintenanceMode.isEnabled()) {
    return <MaintenanceMode />;
  }

  return (
    <Provider store={store}>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <TranslationProvider>
          <WebSocketManager />
          <AppRoutes />
          <div
            ref={supportButtonRef}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUpOrCancel}
            onPointerCancel={handlePointerUpOrCancel}
            onClick={handleButtonClick}
            className="fixed z-20 flex items-center cursor-pointer justify-center bg-[#2283FF] p-4 rounded-full shadow-lg hover:bg-blue-600 transition-colors select-none"
            style={{
              width: `${BUTTON_SIZE_PX}px`,
              height: `${BUTTON_SIZE_PX}px`,
              // Anchored to the side via right/bottom — stays put at all widths.
              // --support-fab-lift (useSupportFabLift) is the height a page's
              // own fixed bottom bar occupies: the icon rises above it instead
              // of being buried underneath (e.g. My Agency's mobile Invite
              // bar). 0 wherever no bar declares itself.
              right: `${renderInset.right}px`,
              bottom: `calc(${renderInset.bottom}px + var(--support-fab-lift, 0px))`,
              cursor: isDragging ? "grabbing" : "grab",
              // touchAction:none stops the browser from scrolling /
              // zooming the page while the user is dragging the icon.
              touchAction: "none",
            }}
            role="button"
            aria-label="Open support"
          >
            <MdSupportAgent className="text-3xl text-white pointer-events-none" />
          </div>

          <ContactSupportModal
            isOpen={showSupportModal}
            onClose={() => setShowSupportModal(false)}
            onSuccess={handleSupportSuccess}
            prefaceMessage={supportPrefaceMessage}
          />

          <SuccessModal
            isOpen={showSuccessModal}
            onClose={() => setShowSuccessModal(false)}
          />

          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: "#fff",
                color: "#333",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                fontSize: "14px",
                padding: "12px 16px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
              },
              success: {
                iconTheme: {
                  primary: "#10b981",
                  secondary: "#fff",
                },
              },
              error: {
                iconTheme: {
                  primary: "#ef4444",
                  secondary: "#fff",
                },
              },
            }}
          />
        </TranslationProvider>
      </BrowserRouter>
    </Provider>
  );
};

export default App;
